<template>
    <div class="container-nzidoo">
        <page-title title="sidebar.productReportsManagement" />

        <LoadingSpinner v-if="loader" />

        <!-- Toolbar -->
        <div class="admin-toolbar">
            <div class="admin-search-box">
                <i class="mdi mdi-magnify admin-search-icon"></i>
                <input type="text" v-model="searchTerm" :placeholder="$t('sentence.searchByName')"
                    class="admin-search-input" />
                <i v-if="searchTerm" class="mdi mdi-close-circle admin-clear-icon" @click="clearSearch"></i>
            </div>

            <!-- Filter by status -->
            <select v-model="filterStatus" class="admin-form-input" style="width:auto;min-width:150px;">
                <option value="">{{ $t('filter.all') }}</option>
                <option value="pending">{{ $t('filter.pending') }}</option>
                <option value="reviewed">{{ $t('filter.reviewed') }}</option>
                <option value="rejected">{{ $t('filter.rejected') }}</option>
            </select>
        </div>

        <!-- Reports List -->
        <div v-if="filteredProducts.length" class="products-section">
            <div class="products-container">
                <div class="table-responsive">
                    <table class="table mb-0">
                        <thead class="thead-light">
                            <tr>
                                <th>{{ $t('attr.product') }}</th>
                                <th>{{ $t('attr.reports') }}</th>
                                <th>{{ $t('attr.username') }}</th>
                                <th>{{ $t('attr.reason') }}</th>
                                <th>{{ $t('attr.message') }}</th>
                                <th class="d-none d-md-table-cell">{{ $t('product.createdAt') }}</th>
                                <th>{{ $t('main.status') }}</th>
                                <th>{{ $t('main.action') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="product in filteredProducts">
                                <tr v-for="(report, index) in product.reports" :key="`${product.id}-${report.id}`"
                                    class="product-row">
                                    <!-- Product (only once) -->
                                    <td v-if="index === 0" :rowspan="product.reports.length">
                                        <div class="product-cell">
                                            <img v-if="product.pictures.length > 0"
                                                :src="storageUrl + product.pictures[0]" class="product-thumbnail"
                                                alt="Product" />
                                            <div class="product-info">
                                                <span class="product-name">
                                                    {{ getProductName(product) }}
                                                    <span v-if="product.active_boost.length > 0"
                                                        class="badge badge-pill status-pending">
                                                        <i class="mdi mdi-rocket-launch"></i>
                                                        {{ $t('ecommerce.product.boosted') }}
                                                    </span>
                                                    <span v-if="product.deleted_at !== null"
                                                        class="badge badge-pill badge-danger text-capitalize">
                                                        {{ $t('status.deleted') }}
                                                    </span>
                                                </span>
                                            </div>
                                        </div>
                                    </td>

                                    <!-- Reports count (only once) -->
                                    <td v-if="index === 0" :rowspan="product.reports.length">
                                        <span class="status-badge" style="background: #fee2e2; color: #991b1b;">
                                            {{ product.reports.length }}
                                        </span>
                                    </td>

                                    <!-- Reporter -->
                                    <td>
                                        <span class="font-weight-semibold">
                                            {{ report.user?.username || '-' }}
                                        </span>
                                    </td>

                                    <!-- Reason -->
                                    <td>
                                        <span class="badge badge-pill text-capitalize"
                                            style="background: #fee2e2; color: #991b1b;">
                                            {{ report.reason }}
                                        </span>
                                    </td>

                                    <!-- Message -->
                                    <td>
                                        <span class="text-muted" style="font-size: 0.875rem;">
                                            {{ report.message }}
                                        </span>
                                    </td>

                                    <!-- Date -->
                                    <td class="d-none d-md-table-cell">
                                        <span class="date-text">
                                            {{ report.created_at | moment }}
                                        </span>
                                    </td>

                                    <!-- Status -->
                                    <td>
                                        <span :class="getStatusBadgeClass(report.status)" class="status-badge">
                                            {{ $t(`filter.${report.status}`) }}
                                        </span>
                                    </td>

                                    <td>
                                        <div class="action-buttons-group">
                                            <button @click="openStatusModal(report, product)"
                                                :class="report.status === 'pending' ? 'btn-action btn-approve' : 'btn-action btn-view'"
                                                :title="report.status === 'pending' ? 'Update Status' : 'View Details'">
                                                <i
                                                    :class="report.status === 'pending' ? 'mdi mdi-list-status' : 'mdi mdi-eye'"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Empty -->
        <div v-else class="admin-empty-state">
            <i class="mdi mdi-inbox"></i>
            <p class="admin-empty-text">{{ $t('sentence.noResults') }}</p>
        </div>

        <!-- Status Update Drawer Backdrop -->
        <transition name="drawer-backdrop">
            <div v-if="showStatusModal" class="admin-drawer-backdrop" @click="closeStatusModal"></div>
        </transition>

        <!-- Status Update Drawer -->
        <transition name="drawer">
            <div v-if="showStatusModal" class="admin-drawer-container">
                <div class="admin-drawer-header">
                    <h3 class="admin-drawer-title">{{ $t('action.update') }}</h3>
                    <button class="btn-close" @click="closeStatusModal">
                        <i class="mdi mdi-close"></i>
                    </button>
                </div>

                <div class="admin-drawer-body" v-if="selectedReport">
                    <!-- Product Images -->
                    <div v-if="selectedReport.product && selectedReport.product.pictures && selectedReport.product.pictures.length > 0"
                        class="detail-section product-images">
                        <h4 class="section-title">{{ $t('attr.product') }} Images</h4>
                        <div class="images-grid">
                            <img v-for="(pic, index) in selectedReport.product.pictures" :key="index"
                                :src="storageUrl + pic" class="product-image" alt="Product" />
                        </div>
                    </div>

                    <!-- Product Information -->
                    <div class="detail-section">
                        <h4 class="section-title">{{ $t('attr.product') }} Information</h4>
                        <div class="detail-grid">
                            <div class="detail-item">
                                <span class="detail-label">{{ $t('attr.product') }} Name</span>
                                <span class="detail-value">{{ selectedReport.product ?
                                    getProductName(selectedReport.product) :
                                    '-' }}</span>
                            </div>
                            <div class="detail-item" v-if="selectedReport.product && selectedReport.product.price">
                                <span class="detail-label">Price</span>
                                <span class="detail-value">{{ selectedReport.product.price }}</span>
                            </div>
                            <div class="detail-item" v-if="selectedReport.product && selectedReport.product.category">
                                <span class="detail-label">Category</span>
                                <span class="detail-value">{{ selectedReport.product.category }}</span>
                            </div>
                            <div class="detail-item" v-if="selectedReport.product && selectedReport.product.condition">
                                <span class="detail-label">Condition</span>
                                <span class="detail-value">{{ selectedReport.product.condition }}</span>
                            </div>
                            <div class="detail-item full-width"
                                v-if="selectedReport.product && selectedReport.product.description">
                                <span class="detail-label">Description</span>
                                <span class="detail-value">{{ selectedReport.product.description }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Product Status</span>
                                <span class="detail-value">
                                    <span v-if="selectedReport.product && selectedReport.product.deleted_at !== null"
                                        class="status-badge" style="background: #fee2e2; color: #991b1b;">Deleted</span>
                                    <span v-else class="status-badge"
                                        style="background: #d1fae5; color: #065f46;">Active</span>
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Report Information -->
                    <div class="detail-section">
                        <h4 class="section-title">Report Information</h4>
                        <div class="detail-grid">
                            <div class="detail-item">
                                <span class="detail-label">{{ $t('attr.username') }}</span>
                                <span class="detail-value">{{ getUserName(selectedReport) }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">{{ $t('attr.reason') }}</span>
                                <span class="detail-value">
                                    <span class="badge badge-pill text-capitalize"
                                        style="background: #fee2e2; color: #991b1b;">
                                        {{ selectedReport.reason }}
                                    </span>
                                </span>
                            </div>
                            <div class="detail-item full-width">
                                <span class="detail-label">{{ $t('attr.message') }}</span>
                                <span class="detail-value">{{ selectedReport.message || '-' }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">{{ $t('product.createdAt') }}</span>
                                <span class="detail-value">{{ selectedReport.created_at | moment }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Status Update Section -->
                    <div class="detail-section" v-if="selectedReport.status === 'pending'">
                        <h4 class="section-title">Update Status</h4>
                        <p style="color: var(--gray-600); font-size: 0.938rem; margin: 0;">
                            Choose an action for this report:
                        </p>
                    </div>
                </div>

                <div class="admin-drawer-footer" v-if="selectedReport && selectedReport.status === 'pending'">
                    <button class="btn btn-success w-100 text-wrap" @click="updateStatusTo('rejected')">
                        <i class="mdi mdi-close-circle"></i>
                        {{ $t('filter.rejected_response') }}
                    </button>
                    <button class="btn btn-danger w-100 text-wrap" @click="updateStatusTo('reviewed')">
                        <i class="mdi mdi-trash-can"></i>
                        {{ $t('filter.reviewed_response') }}
                    </button>
                </div>
                <div class="admin-drawer-footer" v-else>
                    <button class="btn btn-secondary" @click="closeStatusModal">
                        {{ $t('action.close') }}
                    </button>
                </div>
            </div>
        </transition>

        <notifications group="error" />
        <notifications group="success" />
    </div>
</template>

<script>
import PageTitle from '@/components/templates/PageTitle.vue'
import LoadingSpinner from '@/components/templates/LoadingSpinner.vue'
import helper from '@/services/helper'
import config from '@/config'

export default {
    name: 'manageProductReport',
    components: {
        PageTitle,
        LoadingSpinner
    },

    data() {
        return {
            reports: [],
            storageUrl: config.storageUrl,
            loader: false,
            searchTerm: '',
            filterStatus: '',
            showDetailsModal: false,
            showStatusModal: false,
            selectedReport: null,
            statusForm: { status: '' }
        }
    },

    computed: {
        filteredProducts() {
            let list=this.reports

            // Apply status filter
            if (this.filterStatus) {
                list=list.map(product => {
                    const filteredReports=product.reports.filter(r => r.status===this.filterStatus)
                    if (filteredReports.length>0) {
                        return { ...product, reports: filteredReports }
                    }
                    return null
                }).filter(p => p!==null)
            }

            // Apply search filter
            if (this.searchTerm) {
                const t=this.searchTerm.toLowerCase()
                list=list.map(product => {
                    const filteredReports=product.reports.filter(r =>
                        this.getProductName(product).toLowerCase().includes(t)||
                        this.getUserName(r).toLowerCase().includes(t)||
                        r.reason.toLowerCase().includes(t)
                    )
                    if (filteredReports.length>0) {
                        return { ...product, reports: filteredReports }
                    }
                    return null
                }).filter(p => p!==null)
            }

            return list
        }
    },

    mounted() {
        this.loadReports()
    },

    methods: {
        clearSearch() {
            this.searchTerm=''
        },

        getStatusBadgeClass: helper.getStatusBadgeClass,
        getComputedStatus: helper.getComputedStatus,

        getUserName(r) {
            return r.user?.username||r.user?.email||`User #${r.user_id}`
        },

        getProductName(p) {
            return p.name||`Product #${p.id}`
        },

        async loadReports() {
            this.loader=true
            const res=await this.$axios.get('/admin/product-reports')
            this.reports=res.data.data.items;
            this.loader=false;
        },

        openStatusModal(report, product) {
            // Ensure the report has the full product object
            this.selectedReport={
                ...report,
                product: product
            }
            this.statusForm.status=report.status
            this.showStatusModal=true
        },

        closeStatusModal() {
            this.showStatusModal=false
            this.selectedReport=null
        },

        closeDetailsModal() {
            this.showDetailsModal=false
            this.selectedReport=null
        },

        async updateStatus() {
            await this.$axios.put(
                `/admin/product-reports/update-status/${this.selectedReport.id}`,
                this.statusForm
            )
            this.$notify({ group: 'success', title: this.$t('sentence.updateSuccess') })
            this.closeStatusModal()
            this.loadReports()
        },

        async updateStatusTo(status) {
            this.statusForm.status=status
            await this.updateStatus()
        },
    }
}
</script>


<style scoped src="@/assets/css/table-admin-styles.css"></style>